import { FC } from 'react'
import moment from 'moment'
import { ReactComponent as ComparisonReportIcon } from 'src/assets/svg/comparison-report-64.svg'
import { ComparisonHistoryPanel } from 'src/components/ComparisonHistoryPanel'
import { Report } from 'src/charts/YourReports/interfaces/report.interface'
import { CampaignComparisonSummary } from 'src/services/campaignComparisonApi/interfaces/campaignComparison.interface'

interface ComparisonHistoryProps {
  // Campaign id → name, for the ids a row stores. A campaign deleted since the
  // row was saved is simply not in here, and is left out of the row's title —
  // the server leaves it out of the report for the same reason.
  campaignNames: Record<string, string>
  comparisons: CampaignComparisonSummary[]
  isLoading?: boolean
  onAdd: () => void
  onDelete: (id: string) => void
  onDownload: (id: string) => Promise<void>
  onOpen: (id: string) => void
}

const COPY = {
  addNew: 'Add New Comparison',
  // The row's own title is a list of campaign names — too long to read back in
  // a confirmation, and the modal sits directly over the row being deleted.
  deleteTarget: 'this comparison',
  pill: 'Campaign Comparison',
  subtitle: 'Compare campaign performance across different timeframes.',
  title: 'Reporting & Comparison',
}

const CARD_COPY = {
  failedSubtext:
    'We couldn’t generate this comparison due to a temporary issue.',
  failedTitle: (report: Report) => report.name,
  generating: 'Generating comparison…',
  retry: 'Retry Generate Comparison',
}

const DATE_FORMAT = 'MMM D, YYYY'

// Read back in UTC, which is how the window was stored — rendering a
// UTC-midnight boundary in local time would show the day before.
const formatWindow = ({ from, to }: { from: string; to: string }) =>
  `${moment.utc(from).format(DATE_FORMAT)} – ${moment
    .utc(to)
    .format(DATE_FORMAT)}`

export const ComparisonHistory: FC<ComparisonHistoryProps> = ({
  campaignNames,
  comparisons,
  isLoading,
  onAdd,
  onDelete,
  onDownload,
  onOpen,
}) => {
  // A row is named by the campaigns it compares, where a Data Comparison row is
  // named by the two periods it compares one campaign over. That difference also
  // decides what goes underneath: the periods there are the title, so nothing is
  // left to say, while here the title is the campaigns and the window they were
  // all measured over would otherwise go unstated.
  //
  // No file size, and "generated" is when the comparison was saved: the deck is
  // rendered when Download is pressed, so until then there is no file to size.
  const toReport = (comparison: CampaignComparisonSummary): Report => ({
    dateRange: formatWindow(comparison.window),
    generatedAt: moment(comparison.createdAt).format(DATE_FORMAT),
    id: comparison.id,
    modules: [],
    name: comparison.campaigns
      .map((id) => campaignNames[id])
      .filter(Boolean)
      .join(' — '),
    // Nothing is ever in flight or failed: a comparison is a saved definition,
    // and its report is computed when it is opened.
    status: 'ready',
  })

  return (
    <ComparisonHistoryPanel
      addLabel={COPY.addNew}
      deleteTarget={COPY.deleteTarget}
      icon={ComparisonReportIcon}
      isLoading={isLoading}
      onAdd={onAdd}
      onDelete={onDelete}
      onDownload={onDownload}
      onOpen={onOpen}
      // Unreachable: no row is ever `failed`, which is the only state offering a
      // retry. The panel takes it because Data Comparison's rows can be.
      onRetry={() => undefined}
      pillLabel={COPY.pill}
      rowCopy={CARD_COPY}
      rows={comparisons.map(toReport)}
      subtitle={COPY.subtitle}
      title={COPY.title}
    />
  )
}
